import { FC, useMemo } from 'react';
import { Badge, Button, Card, Empty, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { DownloadOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { useAgentActivityLog, useExportAgentActivityLog } from '~/controllers/agentReview';
import { useGetApplication } from '~/controllers/wizard';
import { useHandbook, useHandbookKeys } from '~/controllers/handbook';
import { getDecodedJwt, isAgent, isNogoManager } from '~/utils/getDecodedJwt';
import { getStagePayload } from '~/utils/getStagePayload';
import { Stage } from '~/types/Application';
import { AgentActivityLogEntry } from '~/types/AgentActivityLog';
import { TRACKED_STEP_KEYS, USER_ROLE_LABELS } from '~/constants/agentReview';
import { formatFieldName, localizeValue } from '~/utils/agentActivityLogFormatters';

/** Pricing (fee) entries use JSON-path field names, e.g. "products.ECOM.monthlyFee.amount". */
const isPricingField = (fieldName: string) => fieldName.startsWith('products.');

/** Wizard step key of the agreement/contract-review tab that hosts the fees amendment form. */
const CONTRACT_REVIEW_STEP_KEY = 'review.contractReview';

/** Step key under which the backend records agreement pricing (fee) amendments. */
const PRICING_ACTIVITY_STEP_KEY = 'application.contract.pricing';

/** Readable label for a pricing field path, e.g. "ECOM › monthlyFee › amount". */
const formatPricingFieldName = (fieldName: string) =>
  fieldName.replace(/^products\./, '').replace(/\./g, ' › ');

const AgentActivityLogPanel: FC = () => {
  const { t } = useTranslation('ksaReview');
  const applicationQuery = useGetApplication();
  const stagePayload = getStagePayload.wizard(applicationQuery.data);
  const activeStepKey = stagePayload?.activeStep?.key;
  const handbook = useHandbook().data;
  const handbookKeys = useHandbookKeys();
  const isAgreementTab = activeStepKey === CONTRACT_REVIEW_STEP_KEY;
  const withinReviewStages =
    applicationQuery.data?.stage === Stage.ksaReview ||
    applicationQuery.data?.stage === Stage.ksaContract;
  // Agents and nogo-managers can both view the activity log (the manager is the one who reviews
  // the agent's changes and the fee amendments).
  const canViewActivityLog = isAgent() || isNogoManager();
  const enabled =
    canViewActivityLog &&
    (withinReviewStages || isAgreementTab) &&
    !!activeStepKey &&
    TRACKED_STEP_KEYS.includes(activeStepKey);

  const activityQuery = useAgentActivityLog(enabled, activeStepKey);
  const exportMutation = useExportAgentActivityLog();

  // Fee changes are logged under a dedicated step key; show them only on the agreement/contract-review
  // tab (same tab that renders the pricing form), matching the tab-wise activity-log behaviour.
  const pricingLogEnabled = canViewActivityLog && isAgreementTab;
  const pricingActivityQuery = useAgentActivityLog(pricingLogEnabled, PRICING_ACTIVITY_STEP_KEY);


  const stepEntries = useMemo<AgentActivityLogEntry[]>(() => {
    const reviewEntries = enabled && activityQuery.data ? activityQuery.data : [];
    const pricingEntries =
      pricingLogEnabled && pricingActivityQuery.data ? pricingActivityQuery.data : [];
    return [...reviewEntries, ...pricingEntries]
      .slice()
      .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
  }, [enabled, activityQuery.data, pricingLogEnabled, pricingActivityQuery.data]);



  // Panel shows if either the review step is tracked OR there are fee changes on the agreement tab.
  if (!enabled && !(pricingLogEnabled && (pricingActivityQuery.data?.length ?? 0) > 0)) return null;

  const title = (
    <Space size={8}>
      <Typography.Text strong>{t('Activity log')}</Typography.Text>
      {stepEntries.length > 0 && (
        <Badge
          count={stepEntries.length}
          style={{ backgroundColor: '#eaf2fb', color: '#005996' }}
        />
      )}
    </Space>
  );

  const extra = (
    <Tooltip title={t('Export all logs')}>
      <Button
        type="text"
        size="small"
        icon={<DownloadOutlined />}
        loading={exportMutation.isLoading}
        onClick={() => exportMutation.mutate()}
        aria-label={t('Export all logs')}
      />
    </Tooltip>
  );

  const currentToken = getDecodedJwt();
  const currentUserEmail = currentToken?.email || currentToken?.preferred_username;
  const currentUserName = currentToken?.name ||
    [currentToken?.given_name, currentToken?.family_name].filter(Boolean).join(' ');
  const currentUserRole = isNogoManager() ? USER_ROLE_LABELS.nogoManager : isAgent() ? USER_ROLE_LABELS.agent : undefined;

  return (
    <StyledCard title={title} extra={extra} bordered size="small">
      {activityQuery.isLoading ? (
        <Centered>
          <Spin />
        </Centered>
      ) : !stepEntries.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No changes')} />
      ) : (
        <EntryList>
          {stepEntries.map((entry) => (
            <EntryCard key={entry.id} size="small" bordered>
              <Typography.Text strong style={{ display: 'block', fontSize: 12.5 }}>
                {isPricingField(entry.fieldName)
                  ? formatPricingFieldName(entry.fieldName)
                  : formatFieldName(entry.fieldName, applicationQuery.data?.payload)}
              </Typography.Text>
              <div className="change-stack">
                <Typography.Text type="secondary" delete className="old-line">
                  {isPricingField(entry.fieldName)
                    ? entry.oldValue ?? 'null'
                    : localizeValue(entry.fieldName, entry.oldValue, handbook, handbookKeys) ??
                    'null'}
                </Typography.Text>
                <Typography.Text className="new-line">
                  {isPricingField(entry.fieldName)
                    ? entry.newValue ?? 'null'
                    : localizeValue(entry.fieldName, entry.newValue, handbook, handbookKeys) ??
                    'null'}
                </Typography.Text>
              </div>
              <div className="meta-line">
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(entry.timestamp).format('MMM D, YYYY HH:mm')}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  ·
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {entry.userEmail}
                </Typography.Text>
              </div>
            </EntryCard>
          ))}
        </EntryList>
      )}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  margin-right: 32px;
  max-height: 100%;
  display: flex;
  flex-direction: column;

  .ant-card-head {
    min-height: auto;
    padding: 14px 20px;
  }

  .ant-card-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
  }
`;

const EntryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EntryCard = styled(Card)`
  background: #fafcfe;

  .ant-card-body {
    padding: 10px 12px;
  }

  .change-stack {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12.5px;
  }

  .old-line,
  .new-line {
    display: block;
    word-break: break-word;
  }

  .new-line {
    color: #0a6cb1;
    font-weight: 500;
  }

  .meta-line {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 11px;
  }
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
`;


export default AgentActivityLogPanel;
